import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  SUPPORT_CATEGORIES,
  createSupportTicket,
  listSupportTickets,
  getSupportTicket,
  replySupportTicket,
  getSupportContact,
  type SupportTicketT,
} from '../services/api';

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  open: { label: 'Open', cls: 'bg-primary/10 text-primary' },
  awaiting_user: { label: 'Reply received', cls: 'bg-cta/10 text-cta' },
  resolved: { label: 'Resolved', cls: 'bg-success/10 text-success' },
  closed: { label: 'Closed', cls: 'bg-surface-2 text-text-muted' },
};

function prettyCat(c: string) {
  return c.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function SupportPage() {
  const [tickets, setTickets] = useState<SupportTicketT[]>([]);
  const [active, setActive] = useState<SupportTicketT | null>(null);
  const [contact, setContact] = useState<{ email: string; whatsapp_number: string | null } | null>(null);

  const [category, setCategory] = useState('general');
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [reply, setReply] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadList = async () => {
    try {
      const res = await listSupportTickets();
      setTickets(res.data);
    } catch {
      setError('Could not load your tickets.');
    }
  };

  useEffect(() => {
    loadList();
    getSupportContact().then((r) => setContact(r.data)).catch(() => {});
  }, []);

  const openTicket = async (id: string) => {
    try {
      const res = await getSupportTicket(id);
      setActive(res.data);
    } catch {
      setError('Could not open that ticket.');
    }
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (subject.trim().length < 3 || bodyText.trim().length < 1) {
      setError('Please add a subject and a message.');
      return;
    }
    setBusy(true);
    try {
      const res = await createSupportTicket(category, subject.trim(), bodyText.trim());
      setSubject(''); setBodyText(''); setCategory('general'); setShowForm(false);
      setActive(res.data);
      await loadList();
    } catch {
      setError('Could not submit your ticket. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!active || reply.trim().length < 1) return;
    setBusy(true);
    try {
      await replySupportTicket(active.id, reply.trim());
      setReply('');
      await openTicket(active.id);
      await loadList();
    } catch {
      setError('Could not send your reply.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    'w-full rounded-button border border-border bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="font-heading text-2xl font-bold text-text-primary">Help & Support</h1>
      <p className="mt-1 text-sm text-text-muted">
        We don&apos;t offer live chat — open a ticket and our team replies by email and here.
        We aim to respond within one business day.
      </p>

      {/* Contact channels */}
      {contact && (
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <a
            href={`mailto:${contact.email}`}
            className="rounded-pill bg-surface-2 px-3 py-1.5 text-text-secondary hover:text-primary"
          >
            ✉️ {contact.email}
          </a>
          {contact.whatsapp_number && (
            <a
              href={`https://wa.me/${contact.whatsapp_number.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-pill bg-success/10 px-3 py-1.5 font-medium text-success hover:underline"
            >
              💬 WhatsApp us
            </a>
          )}
          <Link to="/complaints" className="rounded-pill bg-surface-2 px-3 py-1.5 text-text-secondary hover:text-primary">
            Formal complaint
          </Link>
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-button bg-error/10 px-4 py-2 text-sm text-error" role="alert">{error}</div>
      )}

      {/* Detail view */}
      {active ? (
        <div className="mt-6">
          <button onClick={() => setActive(null)} className="text-sm text-primary hover:underline">
            ← Back to my tickets
          </button>
          <div className="mt-3 rounded-card border border-border bg-surface p-5">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-heading text-lg font-bold text-text-primary">{active.subject}</h2>
              <span className={`rounded-pill px-2.5 py-0.5 text-xs font-bold ${STATUS_LABEL[active.status]?.cls ?? ''}`}>
                {STATUS_LABEL[active.status]?.label ?? active.status}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-text-muted">{prettyCat(active.category)}</p>

            <ul className="mt-4 space-y-3">
              {active.messages?.map((m) => (
                <li
                  key={m.id}
                  className={`rounded-card px-4 py-3 text-sm ${
                    m.author === 'admin' ? 'bg-primary/5 border border-primary/20' : 'bg-surface-2'
                  }`}
                >
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                    {m.author === 'admin' ? 'My Bets Support' : 'You'}
                  </p>
                  <p className="whitespace-pre-wrap text-text-secondary">{m.body}</p>
                </li>
              ))}
            </ul>

            {active.status !== 'closed' ? (
              <form onSubmit={handleReply} className="mt-4">
                <textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  className={inputCls}
                  placeholder="Type your reply…"
                />
                <button
                  type="submit"
                  disabled={busy || reply.trim().length < 1}
                  className="mt-2 rounded-button bg-primary px-5 py-2 text-sm font-bold text-white shadow-btn hover:bg-primary-dark disabled:opacity-60"
                >
                  {busy ? 'Sending…' : 'Send reply'}
                </button>
              </form>
            ) : (
              <p className="mt-4 text-sm text-text-muted">This ticket is closed. Open a new one if you still need help.</p>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* New ticket */}
          <div className="mt-6">
            {!showForm ? (
              <button
                onClick={() => setShowForm(true)}
                className="rounded-button bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-btn hover:bg-primary-dark"
              >
                + New support ticket
              </button>
            ) : (
              <form onSubmit={handleCreate} className="rounded-card border border-border bg-surface p-5 space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-primary">Topic</label>
                  <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
                    {SUPPORT_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{prettyCat(c)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-primary">Subject</label>
                  <input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} placeholder="Short summary" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-text-primary">Message</label>
                  <textarea rows={4} value={bodyText} onChange={(e) => setBodyText(e.target.value)} className={inputCls} placeholder="Tell us what's happening. Include any bet or transaction reference." />
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={busy} className="rounded-button bg-primary px-5 py-2 text-sm font-bold text-white shadow-btn hover:bg-primary-dark disabled:opacity-60">
                    {busy ? 'Submitting…' : 'Submit ticket'}
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="rounded-button px-4 py-2 text-sm text-text-muted hover:text-text-primary">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* List */}
          <div className="mt-6">
            <h2 className="mb-2 font-heading text-base font-bold text-text-primary">My tickets</h2>
            {tickets.length === 0 ? (
              <p className="text-sm text-text-muted">You haven&apos;t opened any tickets yet.</p>
            ) : (
              <ul className="space-y-2">
                {tickets.map((t) => (
                  <li key={t.id}>
                    <button
                      onClick={() => openTicket(t.id)}
                      className="flex w-full items-center justify-between rounded-card border border-border bg-surface px-4 py-3 text-left hover:border-primary/40"
                    >
                      <span>
                        <span className="block text-sm font-semibold text-text-primary">{t.subject}</span>
                        <span className="block text-xs text-text-muted">{prettyCat(t.category)}</span>
                      </span>
                      <span className={`rounded-pill px-2.5 py-0.5 text-xs font-bold ${STATUS_LABEL[t.status]?.cls ?? ''}`}>
                        {STATUS_LABEL[t.status]?.label ?? t.status}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
